/**
 * ─── POS Service — Business Logic ────────────────────────────
 * Pure functions for POS operations. No I/O.
 *
 * Handles:
 *  - Product code generation
 *  - Price computation (retail, wholesale, VAT)
 *  - Sale total calculation (subtotal, discounts, VAT, change)
 *  - Shift reconciliation
 *  - Stock adjustment after sale
 *  - Profit margin computation
 */

import type {
  POSProduct,
  POSSale,
  POSSaleItem,
  POSShift,
  PaymentMethod,
} from './pos.schema'

// ═══════════════════════════════════════════════════════════════
// Product Service
// ═══════════════════════════════════════════════════════════════

export class POSProductService {
  /**
   * Generate SKU: POS-YYYYMM-NNNN
   */
  static generateSku(sequenceNumber: number): string {
    const now = new Date()
    const ym = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`
    return `POS-${ym}-${String(sequenceNumber).padStart(4, '0')}`
  }

  /**
   * Compute retail price from cost and markup percent.
   */
  static computeRetailPrice(costPrice: number, markupPercent: number): number {
    return Math.round(costPrice * (1 + markupPercent / 100) * 100) / 100
  }

  /**
   * Compute markup percent from cost and retail.
   */
  static computeMarkupPercent(costPrice: number, retailPrice: number): number {
    if (costPrice <= 0) return 0
    return Math.round(((retailPrice - costPrice) / costPrice) * 10000) / 100
  }

  /**
   * Compute gross profit on a sale item.
   */
  static computeGrossProfit(item: POSSaleItem): number {
    return Math.round((item.lineTotal - item.costPrice * item.quantity) * 100) / 100
  }

  /**
   * Compute margin percent on a sale item.
   */
  static computeMarginPercent(item: POSSaleItem): number {
    if (item.lineTotal <= 0) return 0
    return Math.round((1 - (item.costPrice * item.quantity) / item.lineTotal) * 10000) / 100
  }

  /**
   * Check if product is low stock.
   */
  static isLowStock(product: POSProduct): boolean {
    return product.stockOnHand <= product.minStockLevel
  }

  /**
   * Check if product is out of stock.
   */
  static isOutOfStock(product: POSProduct): boolean {
    return product.stockOnHand <= 0
  }

  /**
   * Get stock status label.
   */
  static getStockStatus(product: POSProduct): 'in_stock' | 'low_stock' | 'out_of_stock' {
    if (product.stockOnHand <= 0) return 'out_of_stock'
    if (product.stockOnHand <= product.minStockLevel) return 'low_stock'
    return 'in_stock'
  }
}

// ═══════════════════════════════════════════════════════════════
// Sale Service
// ═══════════════════════════════════════════════════════════════

export class POSSaleService {
  /**
   * Generate invoice number: INV-YYYYMMDD-NNNN
   */
  static generateInvoiceNumber(sequenceNumber: number): string {
    const now = new Date()
    const d = now.toISOString().slice(0, 10).replace(/-/g, '')
    return `INV-${d}-${String(sequenceNumber).padStart(4, '0')}`
  }

  /**
   * Compute line totals for a sale item.
   * Returns the item with computed lineTotal, discountAmount, vatAmount.
   */
  static computeLineTotals(item: {
    quantity: number
    unitPrice: number
    discountPercent: number
    vatRate: number
    isVatInclusive: boolean
  }): {
    lineTotal: number
    discountAmount: number
    vatAmount: number
    preDiscountTotal: number
  } {
    const preDiscountTotal = Math.round(item.quantity * item.unitPrice * 100) / 100
    const discountAmount = Math.round(preDiscountTotal * (item.discountPercent / 100) * 100) / 100
    const afterDiscount = Math.round((preDiscountTotal - discountAmount) * 100) / 100

    let vatAmount = 0
    if (item.isVatInclusive && item.vatRate > 0) {
      vatAmount = Math.round(afterDiscount * (item.vatRate / (1 + item.vatRate)) * 100) / 100
    }

    const lineTotal = afterDiscount

    return { lineTotal, discountAmount, vatAmount, preDiscountTotal }
  }

  /**
   * Compute sale-level totals from line items.
   */
  static computeSaleTotals(items: POSSaleItem[], saleDiscountPercent: number = 0): {
    subtotal: number
    discountTotal: number
    vatTotal: number
    grandTotal: number
    itemCount: number
  } {
    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0)
    const subtotal = items.reduce((sum, i) => sum + i.lineTotal + i.discountAmount, 0)
    const lineDiscountTotal = items.reduce((sum, i) => sum + i.discountAmount, 0)

    // Sale-level discount on subtotal
    const saleDiscount = Math.round(subtotal * (saleDiscountPercent / 100) * 100) / 100
    const discountTotal = Math.round((lineDiscountTotal + saleDiscount) * 100) / 100

    const vatTotal = items.reduce((sum, i) => sum + i.vatAmount, 0)

    const afterDiscount = Math.round((subtotal - discountTotal) * 100) / 100
    const grandTotal = Math.round(afterDiscount * 100) / 100

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discountTotal,
      vatTotal,
      grandTotal: Math.max(0, grandTotal),
      itemCount,
    }
  }

  /**
   * Compute change from amount tendered.
   */
  static computeChange(amountTendered: number, grandTotal: number): number {
    return Math.max(0, Math.round((amountTendered - grandTotal) * 100) / 100)
  }

  /**
   * Validate that a payment method is valid and sufficient.
   */
  static validatePayment(amountTendered: number, grandTotal: number): {
    valid: boolean
    shortfall: number
    message?: string
  } {
    const shortfall = Math.round((grandTotal - amountTendered) * 100) / 100
    if (shortfall > 0) {
      return { valid: false, shortfall, message: `Insufficient payment. Shortfall: ₱${shortfall.toFixed(2)}` }
    }
    return { valid: true, shortfall: 0 }
  }

  /**
   * Format PHP currency for display.
   */
  static formatPHP(amount: number): string {
    return `₱${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  /**
   * Compute daily sales summary from list of sales.
   */
  static computeDailySummary(sales: POSSale[]): {
    totalSales: number
    transactionCount: number
    totalDiscounts: number
    totalVAT: number
    totalVoided: number
    totalRefunded: number
    byPaymentMethod: Record<string, number>
  } {
    const byPaymentMethod: Record<string, number> = {}
    let totalSales = 0
    let totalDiscounts = 0
    let totalVAT = 0
    let totalVoided = 0
    let totalRefunded = 0

    for (const sale of sales) {
      if (sale.status === 'voided') {
        totalVoided += sale.grandTotal
        continue
      }
      if (sale.status === 'refunded') {
        totalRefunded += sale.grandTotal
        continue
      }

      totalSales += sale.grandTotal
      totalDiscounts += sale.discountTotal
      totalVAT += sale.vatTotal

      const method = sale.paymentMethod
      byPaymentMethod[method] = (byPaymentMethod[method] || 0) + sale.grandTotal
    }

    return {
      totalSales: Math.round(totalSales * 100) / 100,
      transactionCount: sales.filter(s => s.status === 'completed').length,
      totalDiscounts: Math.round(totalDiscounts * 100) / 100,
      totalVAT: Math.round(totalVAT * 100) / 100,
      totalVoided: Math.round(totalVoided * 100) / 100,
      totalRefunded: Math.round(totalRefunded * 100) / 100,
      byPaymentMethod,
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// Shift Service
// ═══════════════════════════════════════════════════════════════

export class POSShiftService {
  /**
   * Generate shift number: SFT-YYYYMMDD-NNNN
   */
  static generateShiftNumber(sequenceNumber: number): string {
    const now = new Date()
    const d = now.toISOString().slice(0, 10).replace(/-/g, '')
    return `SFT-${d}-${String(sequenceNumber).padStart(2, '0')}`
  }

  /**
   * Compute expected closing cash.
   * Formula: openingCash + cashSales - totalPayouts
   */
  static computeExpectedCash(
    openingCash: number,
    paymentBreakdown: Record<string, number>,
    totalPayouts: number,
  ): number {
    const cashSales = paymentBreakdown['cash'] || 0
    return Math.round((openingCash + cashSales - totalPayouts) * 100) / 100
  }

  /**
   * Compute cash discrepancy.
   */
  static computeDiscrepancy(expectedCash: number, actualCash: number): number {
    return Math.round((actualCash - expectedCash) * 100) / 100
  }

  /**
   * Validate shift close.
   */
  static validateClose(shift: POSShift, actualCash: number): {
    valid: boolean
    discrepancy: number
    status: 'balanced' | 'over' | 'short'
    message: string
  } {
    const expected = POSShiftService.computeExpectedCash(
      shift.openingCash,
      shift.paymentBreakdown || {},
      shift.totalPayouts,
    )
    const discrepancy = POSShiftService.computeDiscrepancy(expected, actualCash)

    if (discrepancy === 0) {
      return { valid: true, discrepancy, status: 'balanced', message: 'Cash balanced.' }
    }
    if (discrepancy > 0) {
      return { valid: true, discrepancy, status: 'over', message: `Cash over by ₱${discrepancy.toFixed(2)}` }
    }
    return { valid: true, discrepancy, status: 'short', message: `Cash short by ₱${Math.abs(discrepancy).toFixed(2)}` }
  }
}
